import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", inquiry: "General", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks for reaching out — we'll get back to you within a day.");
      setForm({ name: "", email: "", inquiry: "General", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#F5EFE6] py-24 md:py-32">
      <div className="mx-auto grid max-w-6xl items-start gap-14 px-5 md:grid-cols-2 md:px-8">
        <div>
          <p className="font-accent text-xs font-semibold uppercase tracking-[0.25em] text-[#C86D51]">
            Say Hello
          </p>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-[#2C1D11] sm:text-3xl lg:text-4xl">
            Questions, catering, or just craving a chat?
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#4A3B32]">
            Planning an event, curious about our beans, or want to talk wholesale?
            Drop us a line — a real human from the cafe reads every message.
          </p>
          <div className="mt-10 rounded-3xl border border-dashed border-[#C86D51]/40 bg-[#FDFBF7] p-6">
            <p className="font-serif text-lg italic text-[#3D2817]">
              "The best conversations start over coffee. Ours can start right here."
            </p>
            <p className="mt-3 font-accent text-xs font-semibold uppercase tracking-[0.2em] text-[#C86D51]">
              — The Nomad Team
            </p>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="rounded-3xl bg-[#FDFBF7] p-7 shadow-sm md:p-9"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="mb-2 block font-accent text-xs font-semibold uppercase tracking-wider text-[#3D2817]">
                Name
              </label>
              <Input
                id="contact-name"
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className="rounded-xl border-[#E3D9C9] bg-[#FDFBF7] focus-visible:ring-[#C86D51]"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-2 block font-accent text-xs font-semibold uppercase tracking-wider text-[#3D2817]">
                Email
              </label>
              <Input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className="rounded-xl border-[#E3D9C9] bg-[#FDFBF7] focus-visible:ring-[#C86D51]"
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="mb-2 block font-accent text-xs font-semibold uppercase tracking-wider text-[#3D2817]">
              What's this about?
            </label>
            <Select value={form.inquiry} onValueChange={(v) => setForm({ ...form, inquiry: v })}>
              <SelectTrigger
                data-testid="contact-inquiry-select"
                className="rounded-xl border-[#E3D9C9] bg-[#FDFBF7] focus:ring-[#C86D51]"
              >
                <SelectValue placeholder="Choose a topic" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="General" data-testid="inquiry-option-general">General question</SelectItem>
                <SelectItem value="Catering" data-testid="inquiry-option-catering">Catering & events</SelectItem>
                <SelectItem value="OrderHelp" data-testid="inquiry-option-order-help">Toast order help</SelectItem>
                <SelectItem value="Feedback" data-testid="inquiry-option-feedback">Feedback</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-5">
            <label htmlFor="contact-message" className="mb-2 block font-accent text-xs font-semibold uppercase tracking-wider text-[#3D2817]">
              Message
            </label>
            <Textarea
              id="contact-message"
              data-testid="contact-message-input"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us what's on your mind..."
              className="resize-none rounded-xl border-[#E3D9C9] bg-[#FDFBF7] focus-visible:ring-[#C86D51]"
            />
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-[#C86D51] px-7 py-3.5 font-accent text-sm font-semibold text-[#FDFBF7] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#B35B40] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </section>
  );
};
